<template>
  <div v-if="visible" class="compare-overlay" @click.self="$emit('close')">
    <div class="compare-dialog">
      <div class="compare-header">
        <div>
          <h5 class="compare-title">{{ $t('training.supervisor.compare_title') || 'Comparar rondas' }}</h5>
          <p class="compare-subtitle">
            {{ $t('training.supervisor.compare_subtitle') || 'Selecciona una ronda para cada lado y revísalas en simultáneo.' }}
          </p>
        </div>
        <button type="button" class="compare-close" @click="$emit('close')">×</button>
      </div>

      <div class="compare-prefilter-row">
        <div v-if="prefilterGroups.length" class="compare-prefilter">
          <label class="compare-label">{{ $t('training.round_name') || 'Nombre de la ronda' }}</label>
          <select :value="selectedGroupId" class="compare-select" @change="handleGroupChange($event.target.value)">
            <option value="">{{ $t('training.supervisor.round_filter_placeholder') || 'Selecciona una ronda base' }}</option>
            <option v-for="group in prefilterGroups" :key="`group-${group.id}`" :value="group.id">
              {{ group.label }}
            </option>
          </select>
        </div>

        <div class="compare-mode-row">
          <span class="compare-mode-copy" :class="{ 'compare-mode-copy--active': !isOriginalMode }">
            {{ $t('training.supervisor.compare_mode_judge') || 'Juez' }}
          </span>
          <div class="form-check form-switch compare-mode-switch">
            <input
              id="compare-mode-switch"
              class="form-check-input"
              type="checkbox"
              :checked="isOriginalMode"
              @change="handleModeChange($event.target.checked)"
            >
            <label class="visually-hidden" for="compare-mode-switch">
              {{ $t('training.supervisor.compare_mode_original') || 'Original' }}
            </label>
          </div>
          <span class="compare-mode-copy" :class="{ 'compare-mode-copy--active': isOriginalMode }">
            {{ $t('training.supervisor.compare_mode_original') || 'Original' }}
          </span>
        </div>
      </div>

      <div class="compare-toolbar">
        <div class="compare-select-block">
          <label class="compare-label">{{ $t('training.supervisor.compare_left') || 'Lado izquierdo' }}</label>
          <select
            :value="leftRoundId || ''"
            class="compare-select"
            :disabled="!scopedLeftOptions.length"
            @change="$emit('select-left', $event.target.value)"
          >
            <option value="">{{ selectPlaceholder }}</option>
            <option v-for="option in scopedLeftOptions" :key="`left-${option.id}`" :value="option.id">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="compare-select-block">
          <label class="compare-label">{{ rightSelectLabel }}</label>
          <select
            :value="rightRoundId || ''"
            class="compare-select"
            :disabled="!scopedRightOptions.length"
            @change="$emit('select-right', $event.target.value)"
          >
            <option value="">{{ selectPlaceholder }}</option>
            <option v-for="option in scopedRightOptions" :key="`right-${option.id}`" :value="option.id">
              {{ option.label }}
            </option>
          </select>
        </div>

        <button type="button" class="compare-load-btn" :disabled="!canCompare || loading" @click="$emit('compare')">
          {{ loading ? ($t('training.loading') || 'Cargando rondas...') : ($t('training.supervisor.compare_action') || 'Comparar') }}
        </button>
      </div>

      <div v-if="warningList.length" class="compare-warnings">
        <span v-for="(warning, index) in warningList" :key="`warning-${index}`" class="compare-warning-chip">
          {{ warning }}
        </span>
      </div>

      <p v-if="error" class="compare-error">{{ error }}</p>

      <div
        v-if="(commonInfoItems.length || hasAnyVideo) && !loading"
        class="compare-common-grid"
        :class="{ 'compare-common-grid--with-media': hasAnyVideo }"
      >
        <div v-for="item in commonInfoItems" :key="item.key" class="compare-common-card">
          <span class="compare-common-label">{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </div>

        <button
          v-if="hasAnyVideo"
          type="button"
          class="compare-common-card compare-common-card--video"
          :title="mediaCollapsed
            ? ($t('training.supervisor.compare_expand_video') || 'Mostrar video')
            : ($t('training.supervisor.compare_collapse_video') || 'Ocultar video')"
          :aria-label="mediaCollapsed
            ? ($t('training.supervisor.compare_expand_video') || 'Mostrar video')
            : ($t('training.supervisor.compare_collapse_video') || 'Ocultar video')"
          @click="toggleMediaCollapsed"
        >
          <i class="fa-solid fa-video tech-icon text-primary" aria-hidden="true"></i>
        </button>
      </div>

      <div v-if="sharedVideoUrl && !loading && !mediaCollapsed" class="compare-shared-media">
        <YoutubePlayer
          v-if="isYoutubeUrl(sharedVideoUrl)"
          :video-url="sharedVideoUrl"
          :can-delete="false"
          :read-only="true"
        />
        <LocalVideoPlayer
          v-else
          :video-url="sharedVideoUrl"
          :read-only="true"
        />
      </div>

      <div class="compare-content" :class="{ 'compare-content--loading': loading }">
        <div v-if="loading" class="compare-loading">
          {{ $t('training.loading') || 'Cargando rondas...' }}
        </div>

        <template v-else>
          <div class="compare-column">
            <CompareRoundCard
              side="left"
              :payload="payload && payload.left ? payload.left : null"
              :hide-media="Boolean(sharedVideoUrl) || mediaCollapsed"
            />
          </div>

          <div class="compare-column">
            <CompareRoundCard
              side="right"
              :payload="payload && payload.right ? payload.right : null"
              :hide-media="Boolean(sharedVideoUrl) || mediaCollapsed"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import CompareRoundCard from '@/components/training/CompareRoundCard.vue';
import YoutubePlayer from '@/services/YoutubePlayer.vue';
import LocalVideoPlayer from '@/services/LocalVideoPlayer.vue';

export default {
  name: 'TrainingComparePanel',
  components: {
    CompareRoundCard,
    YoutubePlayer,
    LocalVideoPlayer,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    judgeGroups: {
      type: Array,
      default: () => [],
    },
    originalGroups: {
      type: Array,
      default: () => [],
    },
    originalOptions: {
      type: Array,
      default: () => [],
    },
    mode: {
      type: String,
      default: 'judge',
    },
    leftRoundId: {
      type: [String, Number, null],
      default: null,
    },
    rightRoundId: {
      type: [String, Number, null],
      default: null,
    },
    payload: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'select-left', 'select-right', 'update-mode', 'compare'],
  data() {
    return {
      mediaCollapsed: true,
      selectedGroupId: '',
    };
  },
  computed: {
    prefilterGroups() {
      return (Array.isArray(this.judgeGroups) ? this.judgeGroups : [])
        .filter(group => Array.isArray(group?.options) && group.options.length)
        .map(group => ({
          id: group.id,
          label: group.label,
        }));
    },
    isOriginalMode() {
      return this.mode === 'original';
    },
    canCompare() {
      return Boolean(
        this.leftRoundId
        && this.rightRoundId
        && this.isOptionInSelectedGroup(this.leftRoundId, 'left')
        && this.isOptionInSelectedGroup(this.rightRoundId, 'right')
      );
    },
    availableGroups() {
      const normalizedJudgeGroups = Array.isArray(this.judgeGroups) ? this.judgeGroups : [];
      if (!this.isOriginalMode) {
        return normalizedJudgeGroups
          .filter(group => Array.isArray(group?.options) && group.options.length > 1)
          .map(group => ({
            id: group.id,
            label: group.label,
            leftOptions: group.options,
            rightOptions: group.options,
          }));
      }

      const originalGroupMap = new Map(
        (Array.isArray(this.originalGroups) ? this.originalGroups : [])
          .map(group => [Number(group?.referenceRoundId || 0), group])
          .filter(([referenceRoundId]) => referenceRoundId)
      );

      return normalizedJudgeGroups
        .map((judgeGroup) => {
          const originalGroup = originalGroupMap.get(Number(judgeGroup?.referenceRoundId || 0));
          const leftOptions = Array.isArray(judgeGroup?.options) ? judgeGroup.options : [];
          const rightOptions = Array.isArray(originalGroup?.options) ? originalGroup.options : [];

          if (!leftOptions.length || !rightOptions.length) return null;

          return {
            id: judgeGroup.id,
            label: judgeGroup.label,
            leftOptions,
            rightOptions,
          };
        })
        .filter(Boolean);
    },
    selectedGroup() {
      const groupId = String(this.selectedGroupId || '');
      return this.availableGroups.find(group => String(group.id) === groupId) || null;
    },
    scopedLeftOptions() {
      return this.selectedGroup?.leftOptions || [];
    },
    scopedRightOptions() {
      return this.selectedGroup?.rightOptions || [];
    },
    selectPlaceholder() {
      if (!this.selectedGroupId) {
        return this.$t('training.supervisor.round_filter_placeholder') || 'Selecciona una ronda base';
      }

      return this.$t('training.supervisor.compare_placeholder') || 'Selecciona una ronda';
    },
    rightSelectLabel() {
      if (this.isOriginalMode) {
        return this.$t('training.supervisor.compare_right_original') || 'Round original';
      }

      return this.$t('training.supervisor.compare_right') || 'Lado derecho';
    },
    warningList() {
      const warnings = this.payload?.compare_meta?.warnings;
      return Array.isArray(warnings) ? warnings.filter(Boolean) : [];
    },
    leftVideoUrl() {
      return this.extractVideoUrl(this.payload?.left);
    },
    rightVideoUrl() {
      return this.extractVideoUrl(this.payload?.right);
    },
    sharedVideoUrl() {
      if (!this.leftVideoUrl || !this.rightVideoUrl) return '';
      return this.normalizeVideoUrl(this.leftVideoUrl) === this.normalizeVideoUrl(this.rightVideoUrl)
        ? this.leftVideoUrl
        : '';
    },
    leftRound() {
      return this.payload?.left?.round || null;
    },
    rightRound() {
      return this.payload?.right?.round || null;
    },
    hasAnyVideo() {
      return Boolean(this.leftVideoUrl || this.rightVideoUrl);
    },
    commonInfoItems() {
      const items = [];

      const roundName = this.leftRound?.round_name || this.rightRound?.round_name || '';
      if (roundName) {
        items.push({
          key: 'round_name',
          label: this.$t('training.round_name') || 'Nombre de la ronda',
          value: roundName,
        });
      }

      const athlete = this.formatPerson(this.leftRound?.user || this.rightRound?.user || this.leftRound?.athlete || this.rightRound?.athlete || null);
      if (athlete && athlete !== '—') {
        items.push({
          key: 'athlete',
          label: this.$t('training.supervisor.compare_athlete') || 'Atleta',
          value: athlete,
        });
      }

      const status = this.leftRound?.status?.name || this.rightRound?.status?.name || '—';
      if (status && status !== '—') {
        items.push({
          key: 'status',
          label: this.$t('training.view.status') || 'Estado',
          value: status,
        });
      }

      return items;
    },
  },
  watch: {
    visible(value) {
      this.mediaCollapsed = true;
      if (value) {
        this.ensureSelectedGroup();
      }
    },
    payload() {
      this.mediaCollapsed = true;
    },
    judgeGroups: {
      deep: true,
      handler() {
        this.ensureSelectedGroup();
      },
    },
    originalGroups: {
      deep: true,
      handler() {
        this.ensureSelectedGroup();
      },
    },
    originalOptions: {
      deep: true,
      handler() {
        this.ensureSelectedGroup();
      },
    },
    mode() {
      this.ensureSelectedGroup();
      this.clearSelectionsOutsideGroup();
      this.ensureOriginalRightSelection();
    },
    selectedGroupId() {
      this.ensureOriginalRightSelection();
    },
    leftRoundId(value) {
      this.syncGroupWithRound(value, 'left');
    },
    rightRoundId(value) {
      this.syncGroupWithRound(value, 'right');
    },
  },
  methods: {
    handleModeChange(checked) {
      this.$emit('update-mode', checked ? 'original' : 'judge');
    },
    handleGroupChange(value) {
      this.selectedGroupId = String(value || '');
      this.clearSelectionsOutsideGroup();
      this.ensureOriginalRightSelection();
    },
    findGroupIdByRoundId(roundId, side = 'left') {
      const normalizedId = Number(roundId || 0);
      if (!normalizedId) return '';

      const matchedGroup = this.availableGroups.find(group =>
        ((side === 'right' ? group.rightOptions : group.leftOptions) || []).some(option => Number(option.id) === normalizedId)
      );

      return matchedGroup ? String(matchedGroup.id) : '';
    },
    ensureSelectedGroup() {
      const selectedExistsInPrefilter = this.prefilterGroups.some(group => String(group.id) === String(this.selectedGroupId || ''));
      const preferredGroupId = this.findGroupIdByRoundId(this.leftRoundId, 'left') || this.findGroupIdByRoundId(this.rightRoundId, 'right');

      if (preferredGroupId && preferredGroupId !== String(this.selectedGroupId || '')) {
        this.selectedGroupId = preferredGroupId;
        return;
      }

      if (!selectedExistsInPrefilter) {
        this.selectedGroupId = this.prefilterGroups[0] ? String(this.prefilterGroups[0].id) : '';
      }
    },
    syncGroupWithRound(roundId, side = 'left') {
      const groupId = this.findGroupIdByRoundId(roundId, side);
      if (groupId && groupId !== String(this.selectedGroupId || '')) {
        this.selectedGroupId = groupId;
      }
    },
    isOptionInSelectedGroup(roundId, side = 'left') {
      const normalizedId = Number(roundId || 0);
      if (!normalizedId) return false;

      const sourceOptions = side === 'right' ? this.scopedRightOptions : this.scopedLeftOptions;
      return sourceOptions.some(option => Number(option.id) === normalizedId);
    },
    clearSelectionsOutsideGroup() {
      if (this.leftRoundId && !this.isOptionInSelectedGroup(this.leftRoundId, 'left')) {
        this.$emit('select-left', '');
      }

      if (this.rightRoundId && !this.isOptionInSelectedGroup(this.rightRoundId, 'right')) {
        this.$emit('select-right', '');
      }
    },
    ensureOriginalRightSelection() {
      if (!this.isOriginalMode) return;

      const defaultOption = this.scopedRightOptions[0] || null;
      if (!defaultOption) return;

      if (Number(this.rightRoundId || 0) === Number(defaultOption.id || 0)) {
        return;
      }

      this.$emit('select-right', defaultOption.id);
    },
    extractVideoUrl(payload) {
      return payload?.round?.video_link || payload?.round?.videoUrl || '';
    },
    normalizeVideoUrl(url) {
      const raw = String(url || '').trim();
      if (!raw) return '';

      try {
        const parsed = new URL(raw);
        parsed.hash = '';
        if ((parsed.protocol === 'http:' && parsed.port === '80') || (parsed.protocol === 'https:' && parsed.port === '443')) {
          parsed.port = '';
        }
        if (parsed.pathname.length > 1) {
          parsed.pathname = parsed.pathname.replace(/\/+$/, '');
        }
        parsed.searchParams.sort();
        return parsed.toString();
      } catch (_) {
        return raw.replace(/\/+$/, '');
      }
    },
    isYoutubeUrl(url) {
      return /(?:youtube\.com|youtu\.be)/i.test(String(url || ''));
    },
    formatPerson(person) {
      if (!person) return '—';
      const label = [person.name, person.surname].filter(Boolean).join(' ').trim();
      return label || person.email || (person.id ? `#${person.id}` : '—');
    },
    toggleMediaCollapsed() {
      this.mediaCollapsed = !this.mediaCollapsed;
    },
  },
};
</script>

<style scoped>
.compare-overlay {
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(15, 23, 42, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.compare-dialog {
  width: min(1400px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #f8fafc;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
}

.compare-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.compare-title {
  margin: 0;
  color: #0f172a;
}

.compare-subtitle {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
}

.compare-close {
  border: none;
  background: transparent;
  color: #475569;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
}

.compare-toolbar {
  margin-top: 14px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 10px;
  align-items: end;
}

.compare-prefilter-row {
  margin-top: 14px;
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 14px;
}

.compare-prefilter {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 auto;
  min-width: 0;
}

.compare-mode-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  flex: 0 0 auto;
}

.compare-mode-copy {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
}

.compare-mode-copy--active {
  color: #0f172a;
}

.compare-mode-switch {
  margin: 0;
  display: flex;
  align-items: center;
}

.compare-mode-switch .form-check-input {
  cursor: pointer;
}

.compare-select-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.compare-label {
  color: #475569;
  font-size: 13px;
  font-weight: 600;
}

.compare-select {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
  color: #0f172a;
}

.compare-load-btn {
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  cursor: pointer;
  font-weight: 700;
}

.compare-load-btn {
  background: #1d4ed8;
  color: #fff;
}

.compare-load-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.compare-warnings {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.compare-warning-chip {
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fdba74;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 12px;
}

.compare-error {
  margin: 12px 0 0;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 10px 12px;
}

.compare-common-grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.compare-common-grid--with-media {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.compare-common-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 10px 12px;
}

.compare-common-card--video {
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease, background-color 0.16s ease;
}

.compare-common-card--video:hover {
  border-color: #93c5fd;
  background: #f8fbff;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.12);
  transform: translateY(-1px);
}

.compare-common-card--video:focus-visible {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
}

.compare-common-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.compare-shared-media {
  margin-top: 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
}

.compare-content {
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.compare-content--loading {
  min-height: 240px;
}

.compare-loading {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  color: #475569;
}

.compare-column {
  min-width: 0;
}

@media (max-width: 1100px) {
  .compare-dialog {
    padding: 14px;
    border-radius: 16px;
  }

  .compare-toolbar {
    gap: 8px;
  }

  .compare-content {
    gap: 10px;
  }

  .compare-shared-media {
    padding: 10px;
  }
}

@media (max-width: 820px) {
  .compare-overlay {
    padding: 10px;
  }

  .compare-dialog {
    padding: 12px;
    border-radius: 14px;
  }

  .compare-title {
    font-size: 17px;
  }

  .compare-subtitle,
  .compare-label,
  .compare-warning-chip {
    font-size: 12px;
  }

  .compare-select,
  .compare-load-btn {
    padding: 8px 10px;
    font-size: 13px;
  }

  .compare-content {
    gap: 8px;
  }

  .compare-common-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .compare-common-grid--with-media {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .compare-prefilter-row {
    gap: 10px;
  }
}

@media (max-width: 680px) {
  .compare-dialog {
    padding: 10px;
  }

  .compare-prefilter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .compare-mode-row {
    justify-content: flex-start;
  }

  .compare-toolbar {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .compare-load-btn {
    grid-column: 1 / -1;
    width: 100%;
  }

  .compare-content {
    gap: 6px;
  }

  .compare-common-grid {
    gap: 6px;
  }
}

@media (max-width: 640px) {
  .compare-dialog {
    padding: 10px;
  }

  .compare-common-card {
    border-radius: 10px;
    padding: 8px;
  }

  .compare-common-label {
    font-size: 11px;
  }
}
</style>